import { AnimatedTitle } from '@/components/animated-title'
import { MessageSquare, SquarePen, PanelLeftOpen, PanelLeftClose } from "lucide-react"
import { useState, useEffect, useRef } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { navigateWithThreadPreservation } from "@/utils/thread-navigation"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { SidebarDivider,
  SidebarGlyphSlot,
  SidebarNavItem,
  SidebarThreadItem,
  SidebarDocumentsNav,
  SidebarUpgradeCta,
  SidebarNotice,
  SIDEBAR_GLYPH_SLOT_CLASS,
  UserMenu,
} from "@/components/sidebar"
import {
  DOCUMENT_QUERY_PARAM,
  documentsUrlWithDocument,
} from "@/components/documents/documents-shared"
import {
  SidebarBrandMark,
  sidebarBrandUsesExpandMark,
} from "@/components/sidebar/sidebar-brand-mark"
import {
  getSidebarBrandConfig,
  sidebarBrandIsBoxed,
} from "@/lib/sidebar-brand"
import { EmployerOrganisationSwitcherItems } from "@/components/sidebar/employer-organisation-switcher"
import { getSidebarIcon } from "@/components/sidebar/sidebar-icon-registry"
import { cn } from "@/lib/utils"
import { authorizedFetch } from "@/lib/api"
import { useAuth } from "@/hooks/use-auth"
import { useAccountMenuIdentity } from "@/hooks/use-account-menu-identity"
import { useFocusStore, UserFocus } from "@/hooks/use-focus-store"
import { useThreads } from "@/hooks/use-threads"
import { useDocumentPins } from "@/hooks/use-documents"
import { useQueryClient } from "@tanstack/react-query"
import {
  CURRENT_CLIENT,
  EMPLOYER_ORGANISATION_HUB_PATH,
  employerOrganisationHubHref,
  isDocumentsEnabled,
} from "@/constants"
import { resolveSidebarNav } from "@/constants/sidebar-nav"
import {
  buildSettingsEnterState,
  getSettingsNavItems,
  type SettingsLocationState,
  isSettingsPath,
  pathnameFromReturnLocation,
} from "@/constants/settings-nav"
import {
  employerOrganisationSettingsSlugFromPath,
  getEmployerOrganisationSettingsNavItems,
  isEmployerOrganisationSettingsPath,
} from "@/constants/employer-organisation-settings-nav"
import { getFocusRoute } from "@/utils/focus-routes"
import type { SidebarNavFocusKey } from "@/types/sidebar-nav"
import { applyFocusChangeNavigation } from "@/utils/focus-navigation"
import { employerSidebarNavItemActive } from "@/utils/employer-sidebar-nav"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"

type HideLogoSidebarCollapseToggleProps = {
  visible: boolean
  isCollapsed: boolean
  menuLayout: "horizontal" | "vertical"
  onToggle: () => void
}

/** Hover-reveal collapse control for tenants with `layout.hideSidebarLogo`. */
function HideLogoSidebarCollapseToggle({
  visible,
  isCollapsed,
  menuLayout,
  onToggle,
}: HideLogoSidebarCollapseToggleProps) {
  if (!visible) return null

  return (
    <SidebarMenu
      className={cn(
        "shrink-0 p-2 opacity-0 transition-opacity duration-75 ease-out",
        "group-hover/sidebar:opacity-100",
        "has-[[data-sidebar=menu-button]:focus-visible]:opacity-100",
        isCollapsed && "items-center"
      )}
    >
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={(e) => {
            onToggle()
            // Pointer click leaves focus on the button; blur so the row hides on mouse out.
            // Keyboard activation uses detail === 0 — keep focus for a11y.
            if (e.detail > 0) {
              e.currentTarget.blur()
            }
          }}
          tooltip={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          menuLayout={menuLayout}
          className="w-fit"
        >
          <SidebarGlyphSlot>
            {isCollapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </SidebarGlyphSlot>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

interface AppSidebarProps {
  /** When false, hide threads list/nav and do not fetch threads (agent UI off for this client). */
  agentUiEnabled?: boolean
  onNavigateToPath?: (path: string) => void
  onThreadSelect?: (threadId: string) => void
  onRefreshThreads?: () => void
  currentThreadId?: string | null
  onClearCurrentThread?: () => void
  onNewChat?: () => void
  onOpenThreads?: () => void
  onFocusChange?: (focusData: UserFocus | null) => void
  threadCallbacks?: {
    onThreadTitleGenerating?: (data: any) => void
  }
  /** Mobile pane to stash when opening settings (restored on Back to app). */
  activeMobilePane?: "chat" | "page"
  /** Restore mobile chat/page pane after Back to app. */
  onRestoreMobilePane?: (pane: "chat" | "page") => void
  /** Navigate back without mobile surface intent overrides (e.g. App `navigateInPlace`). */
  onNavigateBack?: (path: string) => void
  /** Clears agent chat state before auth logout (defaults to `useAuth().logout`). */
  onLogout?: () => void
}

// Focus interface

const isNavActive = (pathname: string, path: string) =>
  pathname === path || (path !== "/" && pathname.startsWith(path + "/"))

export function AppSidebar({
  agentUiEnabled = true,
  onNavigateToPath,
  onThreadSelect,
  onRefreshThreads,
  currentThreadId,
  onClearCurrentThread,
  onNewChat,
  onOpenThreads,
  onFocusChange,
  threadCallbacks,
  activeMobilePane,
  onRestoreMobilePane,
  onNavigateBack,
  onLogout,
}: AppSidebarProps = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = location
  const [searchParams] = useSearchParams()
  const openDocumentId = searchParams.get(DOCUMENT_QUERY_PARAM)
  const { token, isAuthenticated, logout: authLogout } = useAuth()
  const logout = onLogout ?? authLogout
  const accountIdentity = useAccountMenuIdentity()
  const queryClient = useQueryClient()
  const { state, effectiveCollapsed, toggleSidebar, isMobile: sidebarIsMobile, setOpenMobile } = useSidebar()
  const { threads, isLoading: threadsLoading } = useThreads({ enabled: agentUiEnabled })
  const documentsEnabled = isDocumentsEnabled()
  const { data: documentPins = [] } = useDocumentPins({ enabled: isAuthenticated && documentsEnabled })
  const [generatingTitles, setGeneratingTitles] = useState<Set<string>>(new Set())
  const { groups, focus, isFetching, selfServiceLocked, roleSwitchingEnabled, fetchFocus, fetchGroups, updateGroups, setFocus } = useFocusStore()
  const employerOrgCtx = useEmployerOrganisationOptional()
  const sidebarInitializedRef = useRef(false)
  const lastReportedFocusRef = useRef<{ current_focus: string | null; available_foci: string[] } | null>(null)

  const isCollapsed = state === "collapsed"
  const isSettingsMode = isSettingsPath(pathname)
  const isOrgSettingsMode = isEmployerOrganisationSettingsPath(pathname)

  useEffect(() => {
    setGeneratingTitles(new Set())
  }, [threads.length])

  // Backup initialization: Ensure groups/focus are loaded when authenticated
  // This provides a fallback if useFocusStore's useEffect doesn't trigger reliably
  useEffect(() => {
    console.log('🔵 AppSidebar: Backup initialization useEffect running', {
      isAuthenticated,
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
      sidebarInitialized: sidebarInitializedRef.current,
      groupsCount: groups.length
    })
    
    // Simple check: if authenticated with token but no groups, fetch them
    if (isAuthenticated && token && groups.length === 0) {
      // Only trigger if we haven't already initialized (to prevent infinite loops)
      if (!sidebarInitializedRef.current) {
        console.log('🟢 AppSidebar: Triggering backup initialization - fetching groups and focus')
        sidebarInitializedRef.current = true
        console.log('🟢 AppSidebar: Calling fetchGroups()')
        fetchGroups().catch(err => console.error('❌ AppSidebar: fetchGroups error:', err))
        console.log('🟢 AppSidebar: Calling fetchFocus()')
        fetchFocus().catch(err => console.error('❌ AppSidebar: fetchFocus error:', err))
      } else {
        console.log('🟡 AppSidebar: Already attempted initialization, but groups still empty')
      }
    } else if (isAuthenticated && token && groups.length > 0) {
      console.log('✅ AppSidebar: Groups loaded successfully, marking as initialized')
      sidebarInitializedRef.current = true
    } else if (!isAuthenticated) {
      console.log('🔴 AppSidebar: Not authenticated, resetting initialization flag')
      sidebarInitializedRef.current = false
    } else {
      console.log('🟡 AppSidebar: Conditions not met for backup initialization', {
        isAuthenticated,
        hasToken: !!token,
        sidebarInitialized: sidebarInitializedRef.current,
        groupsCount: groups.length
      })
    }
  }, [isAuthenticated, token, groups.length, fetchGroups, fetchFocus])

  // Set up thread title generating callback
  useEffect(() => {
    if (threadCallbacks?.onThreadTitleGenerating) {
      // Replace the callback to also update local state
      const originalCallback = threadCallbacks.onThreadTitleGenerating
      threadCallbacks.onThreadTitleGenerating = (data: any) => {
        console.log('🎯 [SIDEBAR] Thread title generating:', data.thread_id)
        // Add to generating set
        setGeneratingTitles(prev => new Set(prev).add(data.thread_id))
        // Call original callback if needed
        originalCallback(data)
      }
    }
  }, [threadCallbacks])

  // Notify parent only when focus meaningfully changes (avoids cascading URL sync on every focus object identity change)
  useEffect(() => {
    if (!focus || !onFocusChange) return
    const prev = lastReportedFocusRef.current
    const currentFocus = focus.current_focus ?? null
    const currentFoci = focus.available_foci?.join(',') ?? ''
    const prevFocus = prev?.current_focus ?? null
    const prevFoci = prev?.available_foci?.join(',') ?? ''
    if (currentFocus !== prevFocus || currentFoci !== prevFoci) {
      lastReportedFocusRef.current = { current_focus: currentFocus, available_foci: focus.available_foci ?? [] }
      console.log('🎯 AppSidebar: Focus meaningfully changed, notifying parent:', { current_focus: currentFocus, available_foci: focus.available_foci })
      onFocusChange(focus)
    }
  }, [focus, onFocusChange])

  const dismissMobileSidebar = () => {
    if (sidebarIsMobile) {
      setOpenMobile(false)
    }
  }

  const handleThreadClick = (threadId: string) => {
    console.log('📂 [SIDEBAR THREAD CLICK] Thread clicked:', {
      threadId,
      currentURL: window.location.href,
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      threadIdFromURL: new URLSearchParams(window.location.search).get('thread'),
      threadIdFromSearchParams: searchParams.get('thread'),
      currentThreadId
    });
    if (onThreadSelect) {
      onThreadSelect(threadId);
      dismissMobileSidebar()
    }
  };

  const handlePathNavigation = (path: string) => {
    const destination =
      path === EMPLOYER_ORGANISATION_HUB_PATH &&
      employerOrgCtx?.enabled &&
      !employerOrgCtx.organisationsLoading &&
      employerOrgCtx.organisations.length === 0
        ? employerOrganisationHubHref({ openCreate: true })
        : path

    if (
      isSettingsPath(pathname) ||
      isSettingsPath(path) ||
      isEmployerOrganisationSettingsPath(pathname) ||
      isEmployerOrganisationSettingsPath(path)
    ) {
      navigate(destination, { state: location.state })
    } else if (onNavigateToPath) {
      onNavigateToPath(destination)
    } else {
      navigateWithThreadPreservation(navigate, destination, searchParams)
    }
    dismissMobileSidebar()
  }

  const handleThreadDelete = async (threadId: string, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent thread selection
    console.log('Deleting thread:', threadId)

    try {
      const response = await authorizedFetch(`/api/threads/${threadId}`, {
        method: 'DELETE',
      })

      if (response.ok) {
        console.log('Thread deleted successfully')
        // If the deleted thread is currently selected, clear it
        if (currentThreadId === threadId && onClearCurrentThread) {
          console.log('Clearing current thread view')
          onClearCurrentThread()
        }
        queryClient.invalidateQueries({ queryKey: ['threads'] })
        // Notify parent component
        if (onRefreshThreads) {
          onRefreshThreads()
        }
      } else {
        console.error('Failed to delete thread:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error deleting thread:', error)
    }
  }

  const fetchUserData = async () => {
    console.log('AppSidebar: fetchUserData called - starting sequential fetches')
    await fetchGroups()
    console.log('AppSidebar: fetchGroups completed, now fetching focus')
    await fetchFocus()
    console.log('AppSidebar: fetchUserData - all API calls completed', { focus })
    // Notify parent component of focus load
    if (focus) {
      console.log('AppSidebar: Calling onFocusChange with focus data')
      onFocusChange?.(focus)
    }
  }

  const reportFocusChange = (focusPayload: UserFocus) => {
    lastReportedFocusRef.current = {
      current_focus: focusPayload.current_focus,
      available_foci: focusPayload.available_foci ?? [],
    }
    onFocusChange?.(focusPayload)
  }

  const handleSetUserFocus = async (focusValue: string) => {
    try {
      const data = await setFocus(focusValue)
      if (!data) return

      const focusPayload: UserFocus = {
        current_focus: data.current_focus,
        available_foci: data.available_foci ?? [],
        focus_confirmed: data.focus_confirmed ?? focus?.focus_confirmed ?? false,
      }

      applyFocusChangeNavigation({
        navigate,
        searchParams,
        nextFocus: data.current_focus ?? focusValue,
        focusPayload,
        onFocusChange: reportFocusChange,
        queryClient,
      })
      dismissMobileSidebar()
    } catch (error) {
      console.error('AppSidebar: Error setting user focus:', error)
    }
  }

  const handleUpdateGroups = async (updates: Record<string, boolean>) => {
    const result = await updateGroups(updates)
    if (!result) return result

    const focusPayload: UserFocus = {
      current_focus: result.current_focus,
      available_foci: result.available_foci ?? [],
      focus_confirmed: result.focus_confirmed,
    }

    applyFocusChangeNavigation({
      navigate,
      searchParams,
      nextFocus: result.current_focus,
      focusPayload,
      onFocusChange: reportFocusChange,
      onNavigateToPath,
      queryClient,
    })
    dismissMobileSidebar()
    return result
  }

  const layout = (CURRENT_CLIENT as any).layout;
  const mobileChatActionsInMenu = sidebarIsMobile;
  const contentInset = layout?.contentInset !== false;
  const sidebarVariant = contentInset ? "inset" : "sidebar";
  const menuLayout = layout?.sidebarMenuStyle === "vertical" ? "vertical" : "horizontal";
  const sidebarCollapsible = layout?.sidebarCollapsible === false ? "none" : "icon";
  const menuSelectedStyle = layout?.sidebarMenuSelectedStyle;
  const sidebarBrand = getSidebarBrandConfig()
  const brandBoxed = sidebarBrandIsBoxed(sidebarBrand)
  const brandMarkFillClass = sidebarBrandUsesExpandMark() ? "size-full" : undefined
  const brandChromeClass = cn(
    SIDEBAR_GLYPH_SLOT_CLASS,
    brandBoxed
      ? "rounded-lg bg-muted text-sidebar-primary-foreground"
      : "rounded-none bg-transparent",
  )
  const brandHeaderClass = cn(
    sidebarCollapsible !== "none" &&
      (effectiveCollapsed
        ? "flex w-full flex-row items-center justify-center"
        : "flex flex-row items-center justify-between gap-2"),
  )

  const headerOffset = layout?.headerFullWidth ? "3.5rem" : undefined
  const hideThreadSidebar =
    !agentUiEnabled || layout?.hideThreadsList === true || isSettingsMode || isOrgSettingsMode
  const currentFocusKey = (focus?.current_focus ?? null) as SidebarNavFocusKey | null
  const resolvedNavItems = resolveSidebarNav({
    focus: currentFocusKey,
    availableFoci: focus?.available_foci ?? [],
  })

  const settingsEnterState = (): SettingsLocationState =>
    buildSettingsEnterState({
      from: `${location.pathname}${location.search}`,
      mobilePane: activeMobilePane,
    })

  const finishBackToApp = (state: SettingsLocationState | null) => {
    if (state?.mobilePane) {
      onRestoreMobilePane?.(state.mobilePane)
    }
    dismissMobileSidebar()
  }

  const handleBackToApp = () => {
    const state = location.state as SettingsLocationState | null
    const from = state?.from?.trim()
    if (from && !isSettingsPath(pathnameFromReturnLocation(from))) {
      if (onNavigateBack) {
        onNavigateBack(from)
      } else if (onNavigateToPath) {
        onNavigateToPath(from)
      } else {
        navigate(from)
      }
      finishBackToApp(state)
      return
    }

    const fallback = getFocusRoute(focus?.current_focus ?? null)
    if (onNavigateBack) {
      onNavigateBack(fallback)
    } else if (onNavigateToPath) {
      onNavigateToPath(fallback)
    } else {
      navigateWithThreadPreservation(navigate, fallback, searchParams)
    }
    finishBackToApp(state)
  }

  const handleOpenSettings = (path: string) => {
    navigate(path, { state: settingsEnterState() })
    dismissMobileSidebar()
  }

  return (
    <Sidebar
      collapsible={sidebarCollapsible}
      variant={sidebarVariant}
      rightBorder={layout?.sidebarRightBorder === true}
      headerOffset={headerOffset}
    >
      <div
        className={cn(
          "flex h-full min-h-0 w-full min-w-0 flex-col group/sidebar",
          effectiveCollapsed && sidebarCollapsible !== "none" && "group/logo"
        )}
      >
      {(sidebarIsMobile || !layout?.hideSidebarLogo) && (
        <SidebarHeader className={cn(brandHeaderClass, sidebarIsMobile && "items-start px-4 pt-4")}>
          {sidebarCollapsible !== "none" ? (
            sidebarIsMobile ? (
              <div className="flex min-h-10 min-w-0 items-center">
                <AnimatedTitle logoOnLightBackground logoClassName="inline-block h-8 aspect-[123/27] max-w-full bg-sidebar-foreground" />
              </div>
            ) : (
            <>
              <button
                onClick={toggleSidebar}
                data-sidebar="glyph-slot"
                className={cn(
                  brandChromeClass,
                  brandBoxed && "hover:bg-muted/80",
                  "transition-colors outline-none ring-sidebar-ring focus-visible:ring-2",
                )}
                aria-label={isCollapsed ? "Expand sidebar" : "Logo"}
              >
                {effectiveCollapsed ? (
                  <span className="relative flex size-full items-center justify-center">
                    {sidebarBrandUsesExpandMark() ? (
                      <>
                        {/* Opacity-only crossfade so the shared circle stays rock-steady. */}
                        <SidebarBrandMark
                          className={cn(
                            brandMarkFillClass,
                            "absolute inset-0 m-auto transition-opacity duration-200 ease-out motion-reduce:transition-none",
                            "opacity-100 group-hover/logo:opacity-0",
                          )}
                        />
                        <SidebarBrandMark
                          expandHint
                          className={cn(
                            brandMarkFillClass,
                            "pointer-events-none absolute inset-0 m-auto transition-opacity duration-200 ease-out motion-reduce:transition-none",
                            "opacity-0 group-hover/logo:opacity-100",
                          )}
                        />
                      </>
                    ) : (
                      <>
                        <SidebarBrandMark
                          className={cn(
                            brandMarkFillClass,
                            "transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
                            "opacity-100 group-hover/logo:opacity-0",
                          )}
                        />
                        <PanelLeftOpen
                          className={cn(
                            "pointer-events-none absolute size-4 text-foreground transition-opacity duration-200 ease-out motion-reduce:transition-none",
                            "opacity-0 group-hover/logo:opacity-100",
                          )}
                        />
                      </>
                    )}
                  </span>
                ) : (
                  <SidebarBrandMark className={brandMarkFillClass} />
                )}
              </button>
              {!effectiveCollapsed && (
                <SidebarTrigger className="ml-auto size-8 shrink-0 [&_svg]:size-4" />
              )}
            </>
            )
          ) : (
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <div className="flex items-center">
                    <div data-sidebar="glyph-slot" className={brandChromeClass}>
                      <SidebarBrandMark className={brandMarkFillClass} />
                    </div>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          )}
        </SidebarHeader>
      )}

      {/* Focus-Specific Navigation */}
      <div className="flex flex-1 flex-col min-h-0">
      <nav aria-label="Primary" className="flex flex-1 flex-col min-h-0">
      {isSettingsMode || isOrgSettingsMode ? (
        <>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarNavItem
                  icon={getSidebarIcon("ArrowLeft")}
                  label="Back to app"
                  path="/"
                  isActive={false}
                  onClick={handleBackToApp}
                  tooltip="Return to the previous screen"
                  menuLayout={menuLayout}
                  isCollapsed={effectiveCollapsed}
                  selectedStyle={menuSelectedStyle}
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          {isSettingsMode ? (
            <SidebarGroup className="pt-0">
              <SidebarGroupContent>
                <SidebarMenu className={cn(menuLayout === "horizontal" && "gap-1.5")}>
                  {getSettingsNavItems().map((item) => (
                    <SidebarNavItem
                      key={item.path}
                      icon={getSidebarIcon(item.icon)}
                      label={item.label}
                      path={item.path}
                      isActive={isNavActive(pathname, item.path)}
                      onClick={() => handlePathNavigation(item.path)}
                      tooltip={item.description ?? item.label}
                      menuLayout={menuLayout}
                      isCollapsed={effectiveCollapsed}
                      selectedStyle={menuSelectedStyle}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ) : isOrgSettingsMode ? (
            <SidebarGroup className="pt-0">
              <SidebarGroupContent>
                <SidebarMenu className={cn(menuLayout === "horizontal" && "gap-1.5")}>
                  {getEmployerOrganisationSettingsNavItems({
                    slug: employerOrganisationSettingsSlugFromPath(pathname),
                  }).map((item) => (
                    <SidebarNavItem
                      key={item.path}
                      icon={getSidebarIcon(item.icon)}
                      label={item.label}
                      path={item.path}
                      isActive={isNavActive(pathname, item.path)}
                      onClick={() => handlePathNavigation(item.path)}
                      tooltip={item.description ?? item.label}
                      menuLayout={menuLayout}
                      isCollapsed={effectiveCollapsed}
                      selectedStyle={menuSelectedStyle}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ) : null}
          <div className="flex-1 min-h-0" />
        </>
      ) : (
        <>
      {isAuthenticated && focus && (
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className={cn(menuLayout === "horizontal" && "gap-1.5")}>
              {currentFocusKey === "employer" || currentFocusKey === "interviewer" ? (
                <EmployerOrganisationSwitcherItems />
              ) : null}
              {resolvedNavItems.map((item) => {
                return (
                <SidebarNavItem
                  key={item.path}
                  icon={getSidebarIcon(item.icon)}
                  label={item.label}
                  path={item.path}
                  isActive={
                    currentFocusKey === 'employer'
                      ? employerSidebarNavItemActive(pathname, item)
                      : item.exact
                        ? pathname === item.path || pathname === `${item.path}/`
                        : isNavActive(pathname, item.path)
                  }
                  onClick={() => handlePathNavigation(item.path)}
                  tooltip={item.description ?? item.label}
                  menuLayout={menuLayout}
                  isCollapsed={effectiveCollapsed}
                  selectedStyle={menuSelectedStyle}
                />
              )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {isAuthenticated && documentsEnabled && (
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarDocumentsNav
                label={(CURRENT_CLIENT as any).routes?.['/documents']?.label ?? 'Documents'}
                description={
                  (CURRENT_CLIENT as any).routes?.['/documents']?.description ??
                  'Shared markdown pages'
                }
                documentPins={documentPins}
                openDocumentId={openDocumentId}
                isDocumentsActive={isNavActive(pathname, '/documents')}
                menuLayout={menuLayout}
                isCollapsed={effectiveCollapsed}
                selectedStyle={menuSelectedStyle}
                onNavigateDocuments={() => handlePathNavigation('/documents')}
                onOpenDocument={(id) =>
                  handlePathNavigation(documentsUrlWithDocument(id))
                }
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {isAuthenticated && agentUiEnabled && (mobileChatActionsInMenu || !layout?.memoriesThreadsInHeader) && (
        <>
        {mobileChatActionsInMenu && <SidebarDivider />}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {mobileChatActionsInMenu && onNewChat ? (
                <SidebarNavItem
                  icon={SquarePen}
                  label="New chat"
                  className="order-first"
                  menuLayout={menuLayout}
                  isCollapsed={effectiveCollapsed}
                  onClick={() => {
                    dismissMobileSidebar()
                    onNewChat()
                  }}
                />
              ) : null}
              <SidebarNavItem
                icon={MessageSquare}
                label={(CURRENT_CLIENT as any).routes?.["/threads"]?.label ?? "Threads"}
                path="/threads"
                isActive={isNavActive(pathname, "/threads")}
                onClick={() => {
                  if (mobileChatActionsInMenu && onOpenThreads) {
                    dismissMobileSidebar()
                    onOpenThreads()
                  } else {
                    handlePathNavigation('/threads')
                  }
                }}
                tooltip={(CURRENT_CLIENT as any).routes?.["/threads"]?.description ?? "View all your conversation threads"}
                menuLayout={menuLayout}
                isCollapsed={effectiveCollapsed}
                selectedStyle={menuSelectedStyle}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        </>
      )}

      {!hideThreadSidebar && <SidebarDivider variant="none" />}

      {hideThreadSidebar ? (
        <div className="flex-1 min-h-0" />
      ) : (
      <SidebarContent className="custom-scrollbar !gap-0">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {threadsLoading ? (
                <SidebarMenuItem>
                  <SidebarMenuButton disabled>
                    <MessageSquare className="size-4" />
                    {!effectiveCollapsed && <span>Loading threads...</span>}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : threads.length === 0 ? (
                <SidebarMenuItem>
                  <SidebarMenuButton disabled>
                    <MessageSquare className="size-4" />
                    {!effectiveCollapsed && <span>No threads yet</span>}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : (
                threads.slice(0, 10).map((thread) => (
                  <SidebarThreadItem
                    key={thread.id}
                    thread={{
                      id: thread.id,
                      subject: thread.subject || 'Untitled Thread',
                      created_at: thread.created_at,
                      updated_at: thread.updated_at,
                    }}
                    onClick={handleThreadClick}
                    onDelete={handleThreadDelete}
                    isGenerating={generatingTitles.has(thread.id)}
                    menuLayout={menuLayout}
                    isCollapsed={effectiveCollapsed}
                    isActive={currentThreadId === thread.id}
                  />
                ))
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>
      )}
        </>
      )}
      </nav>

      <HideLogoSidebarCollapseToggle
        visible={
          sidebarCollapsible !== "none" &&
          layout?.hideSidebarLogo === true &&
          !sidebarIsMobile
        }
        isCollapsed={effectiveCollapsed}
        menuLayout={menuLayout}
        onToggle={toggleSidebar}
      />

      {!isSettingsMode && !isOrgSettingsMode && (
        <>
          <SidebarUpgradeCta
            isAuthenticated={isAuthenticated}
            isCollapsed={effectiveCollapsed}
            onOpenSettings={handleOpenSettings}
          />

          <SidebarNotice
            isAuthenticated={isAuthenticated}
            isCollapsed={effectiveCollapsed}
          />

          <SidebarDivider />

          <SidebarFooter className="min-h-10 justify-center pt-1.5 pb-3">
            <UserMenu
              isAuthenticated={isAuthenticated}
              personName={accountIdentity.personName}
              email={accountIdentity.email}
              avatarUrl={accountIdentity.avatarUrl}
              avatarColorSeed={accountIdentity.avatarColorSeed}
              avatarUseSilhouette
              logout={logout}
              groups={groups}
              focus={focus}
              updateGroups={handleUpdateGroups}
              setFocus={handleSetUserFocus}
              fetchUserData={fetchUserData}
              isFetching={isFetching}
              menuLayout={menuLayout}
              isCollapsed={effectiveCollapsed}
              showFocusSwitching={layout?.showFocusSwitching !== false}
              selfServiceLocked={selfServiceLocked}
              roleSwitchingEnabled={roleSwitchingEnabled}
              showMemories={agentUiEnabled}
              onNavigateToPath={handlePathNavigation}
              onOpenSettings={handleOpenSettings}
            />
          </SidebarFooter>
        </>
      )}
      </div>
      </div>
      
      {sidebarCollapsible !== "none" && <SidebarRail />}
    </Sidebar>
  )
}
